import { Control, Text } from 'fabric'
import type { Canvas } from 'fabric'
import {
    RESIZE_CONTROL_POSITION,
    RESIZE_CONTROL,
    MIN_FONT_SIZE,
    MAX_FONT_SIZE,
} from '../../constants'

/**
 * Параметры для расчета масштаба из расстояния
 */
export interface CalculateScaleFromDistanceParams {
    /**
     * Позиция X точки мыши
     */
    mouseX: number
    /**
     * Позиция Y точки мыши
     */
    mouseY: number
    /**
     * Позиция X центра объекта
     */
    objectCenterX: number
    /**
     * Позиция Y центра объекта
     */
    objectCenterY: number
    /**
     * Ширина объекта
     */
    objectWidth: number
    /**
     * Высота объекта
     */
    objectHeight: number
}

/**
 * Расчет масштаба на основе расстояния от центра объекта до точки мыши
 * @param params - параметры для расчета
 * @returns масштаб (0-∞)
 */
export function calculateScaleFromDistance(
    params: CalculateScaleFromDistanceParams
): number {
    const {
        mouseX,
        mouseY,
        objectCenterX,
        objectCenterY,
        objectWidth,
        objectHeight,
    } = params

    // Вычисляем расстояние от центра объекта до точки мыши
    const distX = mouseX - objectCenterX
    const distY = mouseY - objectCenterY
    const dist = Math.sqrt(distX * distX + distY * distY)

    // Вычисляем базовое расстояние (половина диагонали объекта)
    const baseDist = Math.sqrt((objectWidth / 2) ** 2 + (objectHeight / 2) ** 2)

    // Вычисляем масштаб
    return dist / baseDist
}

/**
 * Параметры для рендеринга иконки контрола масштабирования
 */
export interface RenderResizeControlIconParams {
    /**
     * Контекст canvas для рисования
     */
    ctx: CanvasRenderingContext2D
    /**
     * Позиция X для рисования
     */
    left: number
    /**
     * Позиция Y для рисования
     */
    top: number
    /**
     * Размер кнопки (диаметр) в пикселях
     */
    size?: number
    /**
     * Цвет заливки
     */
    fillColor?: string
    /**
     * Цвет обводки
     */
    strokeColor?: string
    /**
     * Толщина обводки кнопки
     */
    strokeWidth?: number
    /**
     * Толщина линий иконки
     */
    iconStrokeWidth?: number
}

/**
 * Рендеринг иконки контрола масштабирования (круглая кнопка с диагональными стрелками)
 * Стрелки указывают направление движения курсора:
 * - Стрелка вправо-вверх (↗) - для увеличения размера
 * - Стрелка влево-вниз (↙) - для уменьшения размера
 * @param params - параметры для рендеринга
 */
export function renderResizeControlIcon(
    params: RenderResizeControlIconParams
): void {
    const {
        ctx,
        left,
        top,
        size = 12,
        fillColor = '#4795FF',
        strokeColor = '#FFFFFF',
        strokeWidth = 2,
        iconStrokeWidth = 1.5,
    } = params

    ctx.save()
    ctx.translate(left, top)

    // Рисуем круглую кнопку (только обводка, без заливки)
    ctx.strokeStyle = fillColor // Используем fillColor для обводки (синий цвет)
    ctx.lineWidth = strokeWidth
    ctx.beginPath()
    ctx.arc(0, 0, size / 2, 0, 2 * Math.PI)
    // Не вызываем ctx.fill() - заливка прозрачная
    ctx.stroke()

    // Рисуем иконку диагональных стрелок (увеличение/уменьшение)
    // Стрелки указывают направление движения курсора для масштабирования
    // Направление: с левого нижнего в правый верхний угол (↗) для увеличения
    ctx.strokeStyle = fillColor // Синий цвет для стрелок
    ctx.lineWidth = iconStrokeWidth

    // Параметры для стрелок (смещение от центра) - увеличены для большей видимости
    const arrowOffset = size / 2.5 // Расстояние наконечника от центра (увеличено)
    const arrowBaseOffset = size / 5 // Расстояние основания от центра (увеличено)
    const arrowWidth = size / 7 // Ширина основания стрелки (увеличено)

    // Стрелка для увеличения (↗) - указывает из левого нижнего в правый верхний
    // Наконечник стрелки в правом верхнем углу, основание в левом нижнем
    const arrowUpRightTipX = arrowOffset
    const arrowUpRightTipY = -arrowOffset

    // Основание стрелки (две точки слева и снизу от наконечника)
    ctx.beginPath()
    ctx.moveTo(arrowUpRightTipX, arrowUpRightTipY) // Наконечник (правый верхний)
    ctx.lineTo(arrowUpRightTipX - arrowBaseOffset, arrowUpRightTipY + arrowWidth) // Левая точка основания
    ctx.lineTo(arrowUpRightTipX - arrowWidth, arrowUpRightTipY + arrowBaseOffset) // Нижняя точка основания
    ctx.closePath()
    ctx.stroke()

    // Стрелка для уменьшения (↙) - указывает из правого верхнего в левый нижний
    // Наконечник стрелки в левом нижнем углу, основание в правом верхнем
    const arrowDownLeftTipX = -arrowOffset
    const arrowDownLeftTipY = arrowOffset

    // Основание стрелки (две точки справа и сверху от наконечника)
    ctx.beginPath()
    ctx.moveTo(arrowDownLeftTipX, arrowDownLeftTipY) // Наконечник (левый нижний)
    ctx.lineTo(arrowDownLeftTipX + arrowWidth, arrowDownLeftTipY - arrowBaseOffset) // Правая точка основания
    ctx.lineTo(arrowDownLeftTipX + arrowBaseOffset, arrowDownLeftTipY - arrowWidth) // Верхняя точка основания
    ctx.closePath()
    ctx.stroke()

    ctx.restore()
}

/**
 * Параметры для создания контрола масштабирования
 */
export interface CreateResizeControlParams {
    /**
     * Позиция X контрола относительно объекта (0-1, где 0.5 - правый край)
     */
    x: number
    /**
     * Позиция Y контрола относительно объекта (-0.5 - верхний край, 0.5 - нижний край)
     */
    y: number
    /**
     * Canvas для рендеринга
     */
    canvas: Canvas | null
    /**
     * Обработчик изменения размера (вызывается после масштабирования)
     */
    onScaling?: () => void
    /**
     * Флаг, указывающий, что масштабирование происходит из ползунка (чтобы избежать циклов)
     */
    isScalingFromSlider?: () => boolean
    /**
     * Размер кнопки контрола
     */
    buttonSize?: number
    /**
     * Цвет заливки кнопки
     */
    fillColor?: string
    /**
     * Цвет обводки кнопки
     */
    strokeColor?: string
}

/**
 * Создание кастомного контрола масштабирования для Fabric.js
 * @param params - параметры для создания контрола
 * @returns экземпляр Control для Fabric.js
 */
export function createResizeControl(
    params: CreateResizeControlParams
): Control {
    const {
        x,
        y,
        canvas,
        onScaling,
        isScalingFromSlider,
        buttonSize = 12,
        fillColor = '#4795FF',
        strokeColor = '#FFFFFF',
    } = params

    return new Control({
        x,
        y,
        cursorStyle: 'nesw-resize', // Диагональ северо-восток → юго-запад (↗↙), соответствует направлению стрелок на иконке
        actionHandler: function (eventData, transformData, mouseX, mouseY) {
            const target = transformData.target

            // Проверяем наличие canvas
            if (!canvas) {
                return true
            }

            // Получаем позицию и размеры объекта
            const objectCenterX = target.left ?? 0
            const objectCenterY = target.top ?? 0
            const objectWidth = target.width ?? 0
            const objectHeight = target.height ?? 0

            // Вычисляем масштаб
            const scale = calculateScaleFromDistance({
                mouseX,
                mouseY,
                objectCenterX,
                objectCenterY,
                objectWidth,
                objectHeight,
            })

            // Ограничиваем масштаб на основе MIN/MAX_FONT_SIZE
            // Если объект - это Text, ограничиваем масштаб диапазоном шрифта
            let clampedScale = scale
            if (target instanceof Text) {
                const currentFontSize = target.fontSize ?? MIN_FONT_SIZE
                const newFontSize = currentFontSize * scale

                // Ограничиваем новый размер шрифта диапазоном MIN_FONT_SIZE - MAX_FONT_SIZE
                const clampedFontSize = Math.max(
                    MIN_FONT_SIZE,
                    Math.min(MAX_FONT_SIZE, newFontSize)
                )

                // Вычисляем ограниченный масштаб
                clampedScale = clampedFontSize / currentFontSize
            }

            // Применяем ограниченный масштаб к объекту
            target.set({
                scaleX: clampedScale,
                scaleY: clampedScale,
            })
            target.setCoords()

            // Не вызываем onScaling() здесь, так как это обрабатывается в 'object:modified'
            // после завершения перетаскивания для синхронизации с ползунком

            canvas.renderAll()

            return true
        },
        render: function (
            ctx: CanvasRenderingContext2D,
            left: number,
            top: number
        ) {
            renderResizeControlIcon({
                ctx,
                left,
                top,
                size: buttonSize,
                fillColor,
                strokeColor,
            })
        },
    })
}

/**
 * Параметры для создания контрола масштабирования с предустановленными параметрами
 */
export interface CreateDefaultResizeControlParams {
    /**
     * Canvas для рендеринга
     */
    canvas: Canvas | null
    /**
     * Обработчик изменения размера
     */
    onScaling: () => void
    /**
     * Флаг, указывающий, что масштабирование происходит из ползунка
     */
    isScalingFromSlider: () => boolean
}

/**
 * Создание контрола масштабирования с предустановленными параметрами
 * Использует стандартную позицию (правый верхний угол)
 * @param params - параметры для создания контрола
 * @returns экземпляр Control для Fabric.js
 */
export function createDefaultResizeControl(
    params: CreateDefaultResizeControlParams
): Control {
    const { canvas, onScaling, isScalingFromSlider } = params

    return createResizeControl({
        x: RESIZE_CONTROL_POSITION.X,
        y: RESIZE_CONTROL_POSITION.Y,
        canvas,
        onScaling,
        isScalingFromSlider,
        buttonSize: RESIZE_CONTROL.BUTTON_SIZE,
        fillColor: RESIZE_CONTROL.FILL_COLOR,
        strokeColor: RESIZE_CONTROL.STROKE_COLOR,
    })
}
